/**
 * Der Basismodell-Dialog: Endpunkt, Kennung, Slotzahl, bekanntes Kontextfenster.
 *
 * Endpunkt und Kennung stehen nicht zur Wahl — der Datensatz entsteht beim ersten
 * Auflösen, mit der Slotzahl aus dem Endpunkt-Wert vorbelegt (ADR-0003). Von Hand
 * geschärft werden nur die beiden Zahlen: die gemessene Slotzahl (`qwen36-27B`
 * hat genau zwei) und die geladene Kontextlänge, die der Endpunkt nicht für jedes
 * Modell nennt.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import {
  Button,
  Chip,
  DataTable,
  Dialog,
  FormGrid,
  FormRow,
  Modal,
  TextField,
} from "../../design-system";
import type { AdminApi, BaseModel, Endpoint } from "../../api/client";
import { NumericCell, StrongCell } from "../cells";
import { ListSection } from "../list";
import { Notice, describe, useLoaded } from "../loading";

interface Catalogue {
  baseModels: BaseModel[];
  endpoints: Endpoint[];
}

export function BaseModelsSection({ client }: { client: AdminApi }): ReactElement {
  const { state, reload } = useLoaded<Catalogue>(async () => ({
    baseModels: await client.baseModels(),
    endpoints: await client.endpoints(),
  }));
  const [edited, setEdited] = useState<BaseModel | null>(null);

  const nameOfEndpoint = (id: number): string =>
    state.kind === "loaded"
      ? (state.value.endpoints.find((one) => one.id === id)?.name ?? String(id))
      : String(id);

  return (
    <>
      <ListSection
        title="Basismodelle"
        glyph="cpu"
        first
        headerRight={
          state.kind === "loaded" ? (
            <Chip>{`${state.value.baseModels.length} bekannt`}</Chip>
          ) : null
        }
      >
        <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {state.kind === "loaded" && state.value.baseModels.length === 0 ? (
            <Notice text="Noch kein Basismodell aufgelöst. Öffnen Sie die Modellliste eines Endpunkts im Agenten-Dialog." />
          ) : null}
        </div>

        {state.kind === "loaded" && state.value.baseModels.length > 0 ? (
          <DataTable
            onRowClick={(_row, index) => setEdited(state.value.baseModels[index] ?? null)}
            columns={[
              // Eine Kennung wie „gemma4-26B-A4B (llama.cpp)" trägt hinten den
              // Ladeweg und vorn das Modell: Sie bricht hinten ab und steht ganz
              // im Titel der Zelle.
              { key: "identifier", label: "Kennung", width: 260, maxWidth: 260 },
              { key: "endpoint", label: "Endpunkt", width: 200, maxWidth: 200 },
              { key: "slots", label: "Slots", align: "right", width: 80 },
              { key: "context", label: "Kontextfenster", align: "right", width: 140 },
            ]}
            rows={state.value.baseModels.map((model) => ({
              cells: {
                identifier: <StrongCell title={model.identifier}>{model.identifier}</StrongCell>,
                endpoint:
                  state.value.endpoints.find((one) => one.id === model.endpointId)?.name ??
                  String(model.endpointId),
                slots: <NumericCell>{model.slots}</NumericCell>,
                context: (
                  <NumericCell>
                    {model.contextWindow === null ? "unbekannt" : String(model.contextWindow)}
                  </NumericCell>
                ),
              },
            }))}
          />
        ) : null}
      </ListSection>

      {edited === null ? null : (
        <BaseModelDialog
          model={edited}
          endpoint={nameOfEndpoint(edited.endpointId)}
          save={(slots, contextWindow) => client.sharpenBaseModel(edited.id, slots, contextWindow)}
          close={() => setEdited(null)}
          saved={() => {
            setEdited(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function BaseModelDialog({
  model,
  endpoint,
  save,
  close,
  saved,
}: {
  model: BaseModel;
  endpoint: string;
  save: (slots: number, contextWindow: number | null) => Promise<BaseModel>;
  close: () => void;
  saved: () => void;
}): ReactElement {
  const [slots, setSlots] = useState(String(model.slots));
  const [contextWindow, setContextWindow] = useState(
    model.contextWindow === null ? "" : String(model.contextWindow),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (): void => {
    setBusy(true);
    setError(null);

    save(Number(slots), contextWindow === "" ? null : Number(contextWindow)).then(
      saved,
      (failure: unknown) => {
        setBusy(false);
        setError(describe(failure));
      },
    );
  };

  return (
    <Modal onClose={close}>
      <Dialog
        title="Basismodell schärfen"
        description={`${model.identifier} an ${endpoint}. Die Slotzahl bildet die Slots der geladenen Instanz ab; wirksam ist das Minimum aus ihr und dem Endpunkt-Wert.`}
        width={520}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button variant="primary" size="lg" disabled={busy} onClick={submit}>
              {busy ? "Wird gespeichert ..." : "Speichern"}
            </Button>
          </>
        }
      >
        <FormGrid labelWidth="var(--grid-label-col)">
          <FormRow label="Slots" htmlFor="base-model-slots">
            <TextField
              id="base-model-slots"
              type="number"
              value={slots}
              onChange={setSlots}
            />
          </FormRow>

          <FormRow label="Kontextfenster" htmlFor="base-model-context">
            <TextField
              id="base-model-context"
              type="number"
              value={contextWindow}
              onChange={setContextWindow}
              placeholder="unbekannt"
            />
          </FormRow>

          {error === null ? null : (
            <FormRow full>
              <Notice text={error} glyph="triangle-alert" color="var(--ac-red)" />
            </FormRow>
          )}
        </FormGrid>
      </Dialog>
    </Modal>
  );
}
